import {
  type MaterialCharacteristics,
  type ProductType,
  type DeepPartial,
} from "@jasper/client-models";
import { Accordion, Box, Button, Stack, Title } from "@mantine/core";

import type { FieldValueProps, ValidationErrors } from "../../types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useItemsMaterialCharacteristicsValidation } from "./useItemsMaterialCharacteristicsValidation";
import { MaterialCharacteristicsForm } from "../material";

interface ItemsMaterialCharacteristics {
  [tooth: number]: MaterialCharacteristics;
}

interface Props extends FieldValueProps<ItemsMaterialCharacteristics> {
  productType: ProductType;
  teeth: number[];
  validateTooth: (
    tooth: number
  ) => ValidationErrors<MaterialCharacteristics> | undefined;
  itemMaterials: { value: string; label: string }[];
  itemShadeTypes: { value: string; label: string }[];
  itemShades: { value: string; label: string }[];
}

export const ItemsMaterialCharacteristicsForm = ({
  value,
  errors,
  onChange,
  onBlur,
  productType,
  teeth,
  ...itemCharacteristicsProps
}: Props) => {
  const { t } = useTranslation("forms");

  const { sortedTeeth, getToothStatus, validateTooth, resetToothValidation } =
    useItemsMaterialCharacteristicsValidation({
      productType,
      teeth,
      toothValidator: itemCharacteristicsProps.validateTooth,
      globalErrors: errors,
    });

  // Track closed teeth rather than opened ones, so newly added teeth start opened.
  const [closedItems, setClosedItems] = useState<string[]>([]);

  const openedItems = teeth
    .map(String)
    .filter(tooth => !closedItems.includes(tooth));

  const handleSubmit = (next: DeepPartial<ItemsMaterialCharacteristics>) => {
    onChange(next);
  };

  return (
    <Accordion variant="separated" multiple={true} value={openedItems}>
      {sortedTeeth?.map(tooth => {
        const { toothErrors, icon, title } = getToothStatus(tooth);

        return (
          <Accordion.Item
            key={`tooth-${tooth}`}
            value={tooth.toString()}
            style={toothErrors ? { borderColor: "red" } : undefined}
          >
            <Accordion.Control
              icon={icon}
              onClick={() => {
                if (openedItems.includes(String(tooth))) {
                  setClosedItems(prevState => [...prevState, String(tooth)]);
                  return;
                }

                setClosedItems(prevState =>
                  prevState.filter(item => item !== String(tooth))
                );
              }}
            >
              <Title c={toothErrors ? "red" : undefined} order={2}>
                {title}
              </Title>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="lg">
                <MaterialCharacteristicsForm
                  value={value?.[tooth]}
                  errors={toothErrors}
                  onBlur={() => onBlur?.()}
                  onChange={itemCharacteristics => {
                    resetToothValidation(tooth);

                    handleSubmit({
                      ...value,
                      [tooth]: itemCharacteristics,
                    });
                  }}
                  {...itemCharacteristicsProps}
                />
                <Box>
                  <Button
                    w="auto"
                    onClick={() => {
                      const isValidatedTooth = validateTooth(tooth);

                      if (!isValidatedTooth) {
                        return;
                      }

                      setClosedItems(prevState => [
                        ...prevState,
                        String(tooth),
                      ]);
                    }}
                  >
                    {t("common.validate")}
                  </Button>
                </Box>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};
